import { TaskListRequestParams, TaskListRequestParamsSchema } from "../forms/filters/task";
import { BoolResponce, UserShortInfo } from "../types";
import { CommentOutput } from "./comment";
import BackendConnect from "./connect";

export interface TaskListItem {
  id: number;
  date_start: string;
  date_end?: number;
  author: UserShortInfo; // Пользователь со статусом 1
  executer?: UserShortInfo; // Пользователь со статусом 2
}

export interface TaskCreationData {
    title: string;
    description?: string;
    date_end?: string;
    is_important?: boolean;
    executer_id?: number;
}

export interface TaskCreationSaveData extends Omit<TaskCreationData, "date_end">  {
  date_end?: number;
}

export interface TaskUpdateData {
  task_id: number;
  title?: string;
  description?: string;
  date_end?: string;
  is_important?: boolean;
  status_id?: number;
}

export interface TaskUpdateDataToSave extends Omit<TaskUpdateData, "date_end"> {
  date_end?: number;
}

export interface CheckListItemData {
  id: number;
  title: string;
  is_completed?: boolean;
}

export interface CheckListData {
  id: number; // 0 для нового
  title: string;
  items: CheckListItemData[];
}

export interface TaskFullData {
  id: number;
  title: string;
  status_id: number;
  is_important?: boolean;
  description?: string;
  date_start: string;
  date_end?: string;
  users: {
    status_id: number;
    info: UserShortInfo;
  }[];
  comments: CommentOutput[];
  checklists: CheckListData[];
}

export interface TaskUserUpdateData {
  task_id: number;
  participant_id: number;
  status_id?: number;
}

export interface TaskCommentData {
  task_id: number;
  comment: string;
}

export interface TaskCommentLikeData {
  comment_id: number;
  value_id: number;
}

export interface ChecklistCreationData {
  task_id: number;
  title: string;
  items: ChecklistItemData[];
}

export interface ChecklistItemData {
  id?: number; // Для новых элементов — 0
  title: string;
  is_completed: boolean;
}

export interface ChecklistItemUpdateData {
  item_id: number;
  completed: boolean;
}

export class BackendTaskService extends BackendConnect {
  protected static instance: BackendTaskService;

  constructor(headers: Record<string, string>) {
    super(headers);
  }

  static getInstance(): BackendTaskService {
    if (!BackendTaskService.instance) {
      BackendTaskService.instance = new BackendTaskService({});
      const bearerToken = localStorage.getItem("bearerToken");
      if (bearerToken) {
        BackendTaskService.instance.setAccessToken(bearerToken);
      }
    }
    return BackendTaskService.instance;
  }

  // Задачи

  public async createTask(
    data: TaskCreationSaveData
  ): Promise<BoolResponce> {
    return this.post("/tasks/create", data, "POST");
  }

  public async updateTask(data: TaskUpdateDataToSave): Promise<BoolResponce> {
    return this.post(`/tasks/update/${data.task_id}`, data, "POST");
  }

  public async updateTaskStatus(
    task_id: number,
    status_id: number
  ): Promise<BoolResponce> {
    return this.post("/tasks/status/update", { task_id, status_id }, "POST");
  }

  public async addUserToTask(
    data: TaskUserUpdateData
  ): Promise<BoolResponce> {
    return this.post("/tasks/adduser", data, "POST");
  }

  public async deleteUserFromTask(
    data: TaskUserUpdateData
  ): Promise<BoolResponce> {
    return this.post("/tasks/deleteuser", data, "POST");
  }

  public async getTaskFullData(task_id: number): Promise<{ task: TaskFullData }> {
    return this.get(`/tasks/fulldata/${task_id}`);
  }

  public async getTasksList(
    params: TaskListRequestParams
  ): Promise<{ tasks: TaskListItem[] }> {
    // Валидация параметров перед запросом
    const validatedParams = TaskListRequestParamsSchema.parse(params);

    // Выполнение GET-запроса с валидированными параметрами
    return this.get("/tasks/list", validatedParams);
  }

  public async getTasksCount(
    params: Partial<TaskListRequestParams>
  ): Promise<{ count: number }> {
    // Валидация параметров перед запросом
    const validatedParams = TaskListRequestParamsSchema.partial().parse(params);

    // Выполнение GET-запроса с валидированными параметрами
    return this.get("/tasks/count", validatedParams);
  }

  // Чеклисты

  public async createChecklist(
    data: ChecklistCreationData
  ): Promise<{ checklist_id: number }> {
    return this.post("/tasks/checklists/create", data, "POST");
  }

  public async deleteChecklist(
    checklist_id: number
  ): Promise<BoolResponce> {
    return this.post(`/tasks/checklists/${checklist_id}`, {});
  }

  public async addChecklistItem(
    checklist_id: number,
    item: ChecklistItemData
  ): Promise<{ item_id: number }> {
    return this.post(
      "/tasks/checklists/item/add",
      { checklist_id, ...item },
      "POST"
    );
  }

  public async deleteChecklistItem(
    item_id: number
  ): Promise<BoolResponce> {
    return this.delete(`/tasks/checklists/item/${item_id}`);
  }

  public async updateChecklistItem(
    data: ChecklistItemUpdateData
  ): Promise<BoolResponce> {
    return this.post("/tasks/checklists/item/mark", data, "PATCH");
  }

  public async getTaskChecklists(
    task_id: number
  ): Promise<{ checklists: any[] }> {
    return this.get(`/tasks/${task_id}/checklists`);
  }
}

export const taskBackend = BackendTaskService.getInstance();
